import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AsyncAutocomplete } from '@spotnana/blocks/src/AsyncAutocomplete';
import { AutocompleteSelectionModes } from '@spotnana/blocks/src/Autocomplete/Autocomplete.types';
import { CountryFlag } from '@spotnana/blocks/src/CountryFlag';
import { DeprecatedOptionWithStringifiedValue } from '@spotnana/blocks/src/SelectRoot/helpers';
import { useAvailableCountryList } from 'obt-common/hooks/useAvailableCountryList';
import { select_input } from './styles';
import { TCountryOption, getRelevantSearchCountries } from './utils';

interface IProps {
  onChange: (option?: TCountryOption) => void;
  currentCountryOption: TCountryOption | undefined;
  errorMessage?: string;
  disabled?: boolean;
}

export function UnfocusedInputContent({ selectedOptions }: { selectedOptions: TCountryOption[] }) {
  const selectedCountryCode = selectedOptions[0].value as string;
  return (
    <div css={select_input}>
      <CountryFlag countryCode={selectedCountryCode} altText={selectedOptions[0].label} />
      {selectedOptions[0].label}
    </div>
  );
}

export function ItemPrefix({ item }: { item: DeprecatedOptionWithStringifiedValue<TCountryOption> }) {
  const countryCode = item.value as string;
  return <CountryFlag countryCode={countryCode} altText={item.label} />;
}

export const CountrySelectShared: FC<IProps> = ({ currentCountryOption, onChange, errorMessage, disabled }) => {
  const { t: tt } = useTranslation('WEB');

  const [searchText, setSearchText] = useState('');

  const availableCountryList = useAvailableCountryList();

  const countryOptions = useMemo(
    () => getRelevantSearchCountries(searchText, availableCountryList),
    [searchText, availableCountryList],
  );

  return (
    <AsyncAutocomplete
      availableOptions={countryOptions}
      setSearchString={setSearchText}
      onChange={(newValue) => onChange(newValue[0])}
      controlledValueOptions={currentCountryOption ? [currentCountryOption] : []}
      helperText={errorMessage}
      error={!!errorMessage}
      selectionMode={AutocompleteSelectionModes.SINGLE}
      aria-label={tt('Country/Region')}
      placeholder={tt('Select Country/Region')}
      slots={{ ItemPrefix }}
      UnfocusedInputContent={UnfocusedInputContent}
      disabled={disabled}
    />
  );
};
